import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  Paper
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Schedule as ScheduleIcon,
  Home as HomeIcon,
  Payment as PaymentIcon,
  Star as StarIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import PageLayout from '../components/PageLayout';
import SectionBlock from '../components/SectionBlock';

const BookingSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get booking data from navigation state (passed from checkout)
  const bookingData = location.state?.bookingData || {};
  
  const {
    bookingId = 'BS-' + Math.random().toString(36).substr(2, 9).toUpperCase(),
    cleaningOption = { label: 'Deep Cleaning Service', newPrice: '199' },
    contactInfo = { name: 'Valued Customer', email: 'customer@email.com', phone: '(555) 123-4567' },
    scheduledDate = new Date().toLocaleDateString(),
    scheduledTime = '10:00 AM',
    address = '123 Main St, Orlando, FL 32801',
    message = '',
    paymentStatus = 'paid'
  } = bookingData;

  const handleBookAnother = () => {
    navigate('/');
  };

  const handleViewBookings = () => {
    navigate('/bookings');
  };

  return (
    <PageLayout>
      <Box sx={{ maxWidth: '100vw', overflowX: 'hidden' }}>
        
        {/* Success Hero Section */}
        <SectionBlock
          bgcolor="success.light"
          py={{ xs: 4, md: 6 }}
          maxWidth="md"
          align="center"
        >
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, type: 'spring', stiffness: 200 }}
          >
            <CheckCircleIcon 
              sx={{ 
                fontSize: { xs: 80, md: 100 }, 
                color: 'success.main',
                mb: 2
              }} 
            />
          </motion.div>
          
          <Typography 
            variant="h3" 
            fontWeight={800} 
            color="success.dark"
            gutterBottom
            sx={{ fontSize: { xs: '2rem', md: '3rem' } }}
          >
            Booking Confirmed!
          </Typography>
          
          <Typography 
            variant="h6" 
            color="success.dark" 
            sx={{ mb: 3, fontSize: { xs: '1.1rem', md: '1.3rem' } }}
          >
            Thank you for choosing our cleaning service. Your booking has been successfully confirmed.
          </Typography>
          
          <Chip 
            label={`Booking ID: ${bookingId}`}
            color="success"
            variant="outlined"
            sx={{ fontSize: '1rem', py: 2, px: 1 }}
          />
        </SectionBlock>

        {/* Booking Details Section */}
        <SectionBlock
          bgcolor="background.paper"
          title="Booking Details"
          subtitle="Here's a summary of your confirmed cleaning service"
          py={{ xs: 4, md: 6 }}
          maxWidth="md"
        >
          <Grid container spacing={3}>
            {/* Service Details Card */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <HomeIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      Service Details
                    </Typography>
                  </Stack>
                  
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Service Type
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {cleaningOption.label}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Total Amount
                      </Typography>
                      <Typography variant="h6" color="primary.main" fontWeight={600}>
                        ${cleaningOption.newPrice}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Payment Status
                      </Typography>
                      <Chip 
                        label={paymentStatus === 'paid' ? 'Paid' : 'Pending'}
                        color={paymentStatus === 'paid' ? 'success' : 'warning'}
                        size="small"
                        icon={<PaymentIcon />}
                      />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Schedule & Contact Card */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <ScheduleIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      Schedule & Contact
                    </Typography>
                  </Stack>
                  
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Scheduled Date & Time
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {scheduledDate} at {scheduledTime}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Address
                      </Typography>
                      <Typography variant="body1">
                        {address}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Contact Information
                      </Typography>
                      <Typography variant="body2">
                        {contactInfo.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {contactInfo.email}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {contactInfo.phone}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Special Instructions */}
            {message && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Special Instructions
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {message}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </SectionBlock>

        {/* What Happens Next Section */}
        <SectionBlock
          bgcolor="grey.50"
          title="What Happens Next?"
          subtitle="Here's what you can expect from our team"
          py={{ xs: 4, md: 6 }}
          maxWidth="md"
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <EmailIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Confirmation Email
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You'll receive a detailed confirmation email with all booking information within the next few minutes.
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <PhoneIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Pre-Service Call
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Our team will call you 24 hours before your scheduled service to confirm details and answer any questions.
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <StarIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Quality Service
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Our professional cleaners will arrive on time and deliver exceptional service with our 100% satisfaction guarantee.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </SectionBlock>

        {/* Important Information Alert */}
        <SectionBlock
          bgcolor="background.paper"
          py={{ xs: 3, md: 4 }}
          maxWidth="md"
        >
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight={500}>
              Need to make changes to your booking?
            </Typography>
            <Typography variant="body2">
              Please contact us at least 24 hours before your scheduled service. 
              Call us at (407) 123-4567 or email team@5fairiescleaning.com
            </Typography>
          </Alert>
        </SectionBlock>

        {/* Action Buttons Section */}
        <SectionBlock
          bgcolor="primary.light"
          py={{ xs: 4, md: 6 }}
          maxWidth="sm"
          align="center"
        >
          <Typography variant="h5" fontWeight={600} color="primary.dark" gutterBottom>
            What would you like to do next?
          </Typography>
          
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            justifyContent="center"
            sx={{ mt: 3 }}
          >
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleViewBookings}
              sx={{ px: 4, py: 1.5 }}
            >
              View My Bookings
            </Button>
            
            <Button
              variant="outlined"
              color="primary"
              size="large"
              onClick={handleBookAnother}
              sx={{ px: 4, py: 1.5 }}
            >
              Book Another Service
            </Button>
            
            <Button
              variant="text"
              color="primary"
              size="large"
              startIcon={<ShareIcon />}
              sx={{ px: 4, py: 1.5 }}
            >
              Share Experience
            </Button>
          </Stack>
        </SectionBlock>

        {/* Customer Support Section */}
        <SectionBlock
          bgcolor="background.paper"
          title="Need Help?"
          subtitle="Our customer support team is here to assist you"
          py={{ xs: 4, md: 6 }}
          maxWidth="sm"
          align="center"
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
            <Box textAlign="center">
              <PhoneIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6" fontWeight={600}>
                Call Us
              </Typography>
              <Typography variant="body1" color="primary.main">
                (407) 123-4567
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Mon-Fri: 8AM-8PM<br />
                Sat-Sun: 9AM-6PM
              </Typography>
            </Box>
            
            <Box textAlign="center">
              <EmailIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6" fontWeight={600}>
                Email Us
              </Typography>
              <Typography variant="body1" color="primary.main">
                team@5fairiescleaning.com
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We respond within<br />
                2 hours during business hours
              </Typography>
            </Box>
          </Stack>
        </SectionBlock>

      </Box>
    </PageLayout>
  );
};

export default BookingSuccess; 